import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/c54a7b7/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`Patterns are best practice solutions for how a user achieves a goal. They show reusable combinations of components and templates that address common user objectives with sequences and flows.`}</p>
    </PageDescription>
    <h2 {...{
      "id": "first-steps-to-contributing"
    }}>{`First steps to contributing`}</h2>
    <p>{`If you’ve designed a pattern that is not currently in Carbon, contributing it back allows others in the community to refine and use your pattern in their projects.`}</p>
    <p>{`All contributions start by opening a `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-website/issues/new"
      }}>{`Github issue`}</a>{`. Include a detailed description in which you:`}</p>
    <ul>
      <li parentName="ul">{`Define your pattern and explain the rationale`}</li>
      <li parentName="ul">{`Include mockups and/or prototypes of any fidelity`}</li>
      <li parentName="ul">{`Clarify whether it uses existing components, new components, or both`}</li>
      <li parentName="ul">{`Include competitive and comparative analysis, and any inspirations from other products`}</li>
    </ul>
    <p>{`This issue will be the staging ground for the pattern contribution, and you should expect the Carbon core team and the community to weigh in with suggestions.`}</p>
    <p>{`We encourage you to surface work in progress. If you’re not able to complete all of the parts yourself, someone in the community may be able to help.`}</p>
    <h2 {...{
      "id": "how-to-write-a-pattern"
    }}>{`How to write a pattern`}</h2>
    <p>{`These guidelines are to help you prepare a complete and comprehensive pattern for submission to the IBM Design System. Depending on the pattern, you may need to make some adjustments to the structure but your pattern should cover all of the aspects outlined below.`}</p>
    <p>{`We’ve also created a `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-website/blob/master/docs/pattern-template.mdx"
      }}>{`Markdown file`}</a>{` that includes this guidance. Alternatively, you can provide your content to us in a Box note.`}</p>
    <h3 {...{
      "id": "overview"
    }}>{`Overview`}</h3>
    <p>{`This section answers the question: “What problem does this pattern solve?”`}</p>
    <ul>
      <li parentName="ul">{`Define the use of your pattern and what it does.`}</li>
      <li parentName="ul">{`Explain a user’s needs and how the pattern meets those needs.`}</li>
    </ul>
    <p><inlineCode parentName="p">{`Show pattern in visual form`}</inlineCode></p>
    <h4 {...{
      "id": "when-to-use"
    }}>{`When to use`}</h4>
    <p>{`Include a short list describing situations where this pattern could be applied.`}</p>
    <p>{`For example, use this pattern when:`}</p>
    <ul>
      <li parentName="ul">{`Situation 1`}</li>
      <li parentName="ul">{`Situation 2`}</li>
    </ul>
    <h4 {...{
      "id": "when-not-to-use-optional"
    }}>{`When not to use (optional)`}</h4>
    <p>{`If required, include a short explanation about when not to use this pattern.`}</p>
    <h3 {...{
      "id": "the-solution"
    }}>{`The solution`}</h3>
    <p>{`This section explains the pattern in detail. Use a combination of written explanations and visuals to tell the story.`}</p>
    <h4 {...{
      "id": "behaviors-structure-and-functionality"
    }}>{`Behaviors, structure, and functionality`}</h4>
    <ul>
      <li parentName="ul">{`Describe the elements, components, and content that make up the pattern.`}</li>
      <li parentName="ul">{`Describe pattern behaviors, including guidance on interactions, and changes in states and content.`}</li>
      <li parentName="ul">{`Show the pattern in context. Use visuals throughout your written commentary to illustrate your guidance.`}</li>
    </ul>
    <p>{`Different patterns require different visuals to best explain the behaviors and hierarchy. Choose the options that make the most sense for the story you are telling. See our `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-website/wiki/Production-guidelines"
      }}>{`production guidelines`}</a>{` for information about creating assets for the IBM Design System.`}</p>
    <ul>
      <li parentName="ul">{`Use annotated wireframes or sketches to explain the visual hierarchy of components.`}</li>
      <li parentName="ul">{`Create high-level user flows to explain the big picture.`}</li>
      <li parentName="ul">{`Create a functional prototype if that is the best way to illustrate the pattern’s behavior.`}</li>
    </ul>
    <p>{`Provide a `}<a parentName="p" {...{
        "href": "https://www.sketch.com/docs/getting-started/"
      }}>{`Sketch file`}</a>{` with any symbols you’ve created in the development of this pattern.`}</p>
    <h4 {...{
      "id": "best-practices"
    }}>{`Best practices`}</h4>
    <p>{`List out best practices and include any design considerations that help with
making choices.`}</p>
    <ul>
      <li parentName="ul">{`Do’s`}</li>
      <li parentName="ul">{`Dont’s`}</li>
    </ul>
    <h3 {...{
      "id": "other-use-cases"
    }}>{`Other use cases`}</h3>
    <p>{`If there are use cases that require a different solution, include those here with corresponding explanations and visuals. Be sure to explain the reasons for using one solution over another.`}</p>
    <h3 {...{
      "id": "related-patterns-and-components"
    }}>{`Related patterns and components`}</h3>
    <p>{`Which components did you use when building this pattern? List them here.`}</p>
    <p>{`If necessary, clarify any differences between this pattern and related patterns.`}</p>
    <h3 {...{
      "id": "accessibility"
    }}>{`Accessibility`}</h3>
    <p>{`Evaluate your pattern to ensure it meets `}<a parentName="p" {...{
        "href": "/guidelines/accessibility/overview"
      }}>{`accessibility standards`}</a>{` and guidelines, and provide details of compliance.`}</p>
    <p>{`For example, “Users should be able to TAB into the input field of the search box to begin typing and press ENTER to run the search query.”`}</p>
    <h3 {...{
      "id": "references"
    }}>{`References`}</h3>
    <p>{`Help designers understand your process by explaining your rationale for the way you implemented the pattern. Include any research, citations, books or articles that you found helpful.`}</p>
    <h3 {...{
      "id": "feedback"
    }}>{`Feedback`}</h3>
    <p>{`We’ll include a feedback section here to gather questions and comments.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      